import { TimeScope, TimeScopeMode } from './types';
import { t } from '../../services/translation/translation';
import {
  formatDate,
  getDate,
  getDaysInMonth,
  getMonth,
  getWeek,
  getWeeksInYear,
  getYear,
  parseDate,
} from '../../utils/date';

export const getDefaultTimeScopeValues = (): TimeScope => {
  const now = Date.now();

  return getTimeScopeForDate(now);
};

export const getTimeScopeForDate = (date: number): TimeScope => {
  const day = getDate(date);

  return { day, week: getWeek(date), fortnight: day < 16 ? 1 : 2, month: getMonth(date), year: getYear(date) };
};

const adjustScopeForMaxDate = (scope: TimeScope, maxDate?: number) => {
  const newScope = { ...scope };

  if (!maxDate) {
    return newScope;
  }

  const maxDay = getDate(maxDate);
  const maxWeek = getWeek(maxDate);
  const maxFortnight = maxDay < 16 ? 1 : 2;
  const maxMonth = getMonth(maxDate);
  const maxYear = getYear(maxDate);

  if (newScope.year > maxYear) {
    return getTimeScopeForDate(maxDate);
  }

  if (newScope.year === maxYear) {
    if (newScope.month > maxMonth) {
      return getTimeScopeForDate(maxDate);
    }

    if (newScope.month === maxMonth) {
      if (newScope.fortnight > maxFortnight) {
        return getTimeScopeForDate(maxDate);
      }

      if (newScope.day > maxDay) {
        return getTimeScopeForDate(maxDate);
      }
    }

    if (newScope.week > maxWeek) {
      return getTimeScopeForDate(maxDate);
    }
  }

  return newScope;
};

const adjustScopeForMinDate = (scope: TimeScope, minDate?: number) => {
  const newScope = { ...scope };

  if (!minDate) {
    return newScope;
  }

  const minDay = getDate(minDate);
  const minWeek = getWeek(minDate);
  const minFortnight = minDay < 16 ? 1 : 2;
  const minMonth = getMonth(minDate);
  const minYear = getYear(minDate);

  if (newScope.year < minYear) {
    return getTimeScopeForDate(minDate);
  }

  if (newScope.year === minYear) {
    if (newScope.month < minMonth) {
      return getTimeScopeForDate(minDate);
    }

    if (newScope.month === minMonth) {
      if (newScope.fortnight < minFortnight) {
        return getTimeScopeForDate(minDate);
      }

      if (newScope.day < minDay) {
        return getTimeScopeForDate(minDate);
      }
    }

    if (newScope.week < minWeek) {
      return getTimeScopeForDate(minDate);
    }
  }

  return newScope;
};

export const adjustScopeForConstraints = (scope: TimeScope, minDate?: number, maxDate?: number) => {
  let newScope = { ...scope };

  newScope = adjustScopeForMaxDate(newScope, maxDate);
  newScope = adjustScopeForMinDate(newScope, minDate);

  const nbDays = getDaysInMonth(newScope.year, newScope.month);
  if (newScope.day >= nbDays) {
    newScope.day = nbDays;
  }

  const nbWeeks = getWeeksInYear(newScope.year);
  if (newScope.week >= nbWeeks) {
    newScope.week = nbWeeks;
  }

  return newScope;
};

export const getScopeLabel = (scope: TimeScope, mode: TimeScopeMode) => {
  if (mode === 'day' && scope.day != null && scope.month != null && scope.year != null) {
    return formatDate(parseDate(`${scope.year}-${scope.month + 1}-${scope.day}`, 'YYYY-M-D'), t('date.date-longer'));
  }

  if (mode === 'week' && scope.week != null && scope.year != null) {
    return t('common.week-x', { week: scope.week, year: scope.year });
  }

  if (mode === 'fortnight' && scope.fortnight != null && scope.month != null && scope.year != null) {
    return t('invoice-v2-list-screen.fortnight-label', {
      fortnight: scope.fortnight,
      date: formatDate(parseDate(`${scope.year}-${scope.month + 1}`, 'YYYY-M'), 'MMMM YYYY'),
    });
  }

  if (mode === 'month' && scope.month != null && scope.year != null) {
    return formatDate(parseDate(`${scope.year}-${scope.month + 1}`, 'YYYY-M'), 'MMMM YYYY');
  }

  if (mode === 'year' && scope.year != null) {
    return t('common.year-x', { year: scope.year });
  }

  return '';
};
